import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, Check, Gift, ArrowLeft, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/I18nContext";

// Family Bundle: 4x 1TB codes with volume discount (-15%)
// Base price: 60 * 4 = 240 EUR → 15% off = 204 EUR (saves 36 EUR)
const BUNDLE_QTY = 4;
const BUNDLE_LOOKUP = "storage_1tb_lifetime";

export default function FamilyBundle() {
  const { user } = useAuth();
  const { t, locale, currency, formatPrice } = useT();
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/plans").then(({ data }) => {
      const p = data.plans.find((x) => x.lookup_key === BUNDLE_LOOKUP);
      setPlan(p);
    });
  }, []);

  const buy = async () => {
    const buyerEmail = user?.email || email;
    if (!buyerEmail) return toast.error(t("err_email_first"));
    setBusy(true);
    try {
      const suffixMap = { usd: "_usd", gbp: "_gbp", chf: "_chf", cad: "_cad" };
      const finalKey = currency !== "eur" ? `${BUNDLE_LOOKUP}${suffixMap[currency] || ""}` : BUNDLE_LOOKUP;
      const referral_code = localStorage.getItem("nx_ref") || null;
      const { data } = await api.post("/payments/checkout", {
        lookup_key: finalKey,
        email: buyerEmail,
        origin_url: window.location.origin,
        locale,
        quantity: BUNDLE_QTY,
        referral_code,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_payment"));
    } finally {
      setBusy(false);
    }
  };

  const unit = plan?.prices?.[currency] ?? plan?.prices?.eur ?? 0;
  const total = unit * BUNDLE_QTY;
  const discounted = Math.round(total * 0.85);
  const savings = total - discounted;
  const CUR_SYM = { eur: "€", usd: "$", gbp: "£", chf: "CHF ", cad: "CA$" }[currency] || "€";

  const perksIT = [
    "4 codici da 1 TB da regalare a famiglia o amici",
    "Ognuno attiva su un account separato (max 2 dispositivi)",
    "Codici a vita: nessun rinnovo, mai piu",
    "Perfetti come regalo di compleanno o Natale",
  ];
  const perksEN = [
    "4 codes of 1 TB each to gift to family or friends",
    "Each activates on a separate account (max 2 devices)",
    "Lifetime codes: no renewals, ever",
    "Perfect as a birthday or holiday gift",
  ];
  const perks = locale === "en" ? perksEN : perksIT;

  return (
    <div className="min-h-screen text-slate-100">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
        <button onClick={() => nav("/")} className="mb-6 flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300">
          <ArrowLeft className="h-4 w-4" /> {locale === "en" ? "Back to home" : "Torna alla home"}
        </button>

        <div className="grid gap-8 md:grid-cols-2">
          {/* Left: story */}
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs text-amber-300">
              <Gift className="h-3.5 w-3.5" /> {locale === "en" ? "LIMITED BUNDLE" : "BUNDLE LIMITATO"}
            </div>
            <h1 className="mt-4 text-4xl font-extrabold tracking-tight sm:text-5xl">
              {locale === "en" ? "Family Bundle" : "Bundle "}
              <span className="text-gradient">{locale === "en" ? "1 TB x 4" : "Famiglia"}</span>
            </h1>
            <p className="mt-4 max-w-lg text-lg text-slate-300">
              {locale === "en"
                ? "One click, four 1 TB lifetime codes. Save 15% and share cloud storage that never expires."
                : "Un click, quattro codici da 1 TB a vita. Risparmia il 15% e regala storage cloud che non scade mai."}
            </p>

            <ul className="mt-8 space-y-3">
              {perks.map((p) => (
                <li key={p} className="flex items-start gap-3 text-slate-200">
                  <div className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-cyan-400/20 text-cyan-300">
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </div>
                  {p}
                </li>
              ))}
            </ul>
          </div>

          {/* Right: card */}
          <div className="relative">
            <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-cyan-500/20 via-amber-500/10 to-transparent blur-2xl" />
            <div className="glass relative rounded-2xl border border-cyan-500/50 p-8 glow-cyan">
              <div className="flex items-center gap-3">
                <div className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br from-amber-400 to-cyan-500">
                  <Users className="h-6 w-6 text-slate-950" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-400">
                    {locale === "en" ? "Family bundle" : "Bundle famiglia"}
                  </div>
                  <div className="text-lg font-bold">4 × 1 TB</div>
                </div>
              </div>

              <div className="mt-6">
                <div className="text-xs text-slate-500 line-through">{CUR_SYM}{total}</div>
                <div className="flex items-end gap-2">
                  <span data-testid="bundle-price" className="text-5xl font-extrabold text-slate-100">{CUR_SYM}{discounted}</span>
                  <span className="mb-2 rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs font-bold text-emerald-300">
                    -15%
                  </span>
                </div>
                <div className="mt-1 text-xs text-emerald-300">
                  {locale === "en" ? `You save ${CUR_SYM}${savings}` : `Risparmi ${CUR_SYM}${savings}`}
                </div>
              </div>

              {!user && (
                <div className="mt-5">
                  <Label htmlFor="bundle-email" className="text-xs uppercase tracking-widest text-slate-400">
                    {t("pricing_email_label")}
                  </Label>
                  <Input
                    id="bundle-email"
                    data-testid="bundle-email-input"
                    type="email"
                    placeholder="tu@esempio.it"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 border-slate-800 bg-slate-900 text-slate-100"
                  />
                </div>
              )}

              <Button
                onClick={buy}
                disabled={busy || !plan}
                data-testid="bundle-buy-button"
                className="mt-6 w-full bg-gradient-to-r from-amber-400 to-cyan-400 text-slate-950 hover:opacity-90"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                {locale === "en" ? "Buy family bundle" : "Acquista bundle famiglia"}
              </Button>

              <p className="mt-4 text-center text-[11px] text-slate-500">
                {locale === "en"
                  ? "4 one-time redemption codes emailed instantly. Printable PDF gift cards included."
                  : "4 codici di riscatto monouso inviati subito via email. PDF gift card stampabili inclusi."}
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
